import { useState } from "react";
import { X, Calendar, Clock, Play, Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface ScheduleStartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule?: (data: { date: Date; time: string; sendReminder: boolean }) => void;
  treatment?: {
    clientName: string;
    treatmentName: string;
    scheduledDate: string;
    scheduledTime: string;
  };
}

export default function ScheduleStartModal({
  isOpen,
  onClose,
  onSchedule,
  treatment,
}: ScheduleStartModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [sendReminder, setSendReminder] = useState(true);
  const [reminderTime, setReminderTime] = useState("15min");
  const [isDateOpen, setIsDateOpen] = useState(false);

  const reminderOptions = [
    { value: "15min", label: "15 minutes before" },
    { value: "30min", label: "30 minutes before" },
    { value: "1hour", label: "1 hour before" },
    { value: "1day", label: "1 day before" },
  ];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleSchedule = () => {
    onSchedule?.({
      date: selectedDate,
      time: selectedTime,
      sendReminder,
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedDate(new Date());
    setSelectedTime("10:00");
    setSendReminder(true);
    setReminderTime("15min");
  };

  // Quick time slots for today
  const quickTimeSlots = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#f0ebf5] flex items-center justify-center">
              <Play className="w-5 h-5 text-[#704aaf]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2d2d2d]">Schedule Start</h2>
              {treatment && (
                <p className="text-sm text-[#6b6b6b]">{treatment.treatmentName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Treatment Info */}
          {treatment && (
            <div className="p-4 bg-[#f8f5fc] rounded-lg border border-[#e8e0f0]">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Client:</span>
                  <span className="font-medium text-[#2d2d2d]">{treatment.clientName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Treatment:</span>
                  <span className="font-medium text-[#2d2d2d]">{treatment.treatmentName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Originally Scheduled:</span>
                  <span className="font-medium text-[#2d2d2d]">
                    {treatment.scheduledDate} at {treatment.scheduledTime}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Start Date
            </label>
            <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] hover:border-[#704aaf] transition-colors">
                  <Calendar className="w-4 h-4 text-[#704aaf]" />
                  <span>{formatDate(selectedDate)}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-[#e8e0f0] shadow-xl rounded-xl">
                <DateCalendar
                  selectedDate={selectedDate}
                  onSelectDate={(date) => {
                    setSelectedDate(date);
                    setIsDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Start Time
            </label>
            
            {/* Quick Time Slots */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {quickTimeSlots.map((time) => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`py-2 text-sm font-medium rounded-lg transition-colors ${
                    selectedTime === time
                      ? "bg-[#704aaf] text-white"
                      : "bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] hover:bg-[#f0ebf5]"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>

            {/* Custom Time Input */}
            <div className="flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg hover:border-[#704aaf] transition-colors">
              <Clock className="w-4 h-4 text-[#704aaf]" />
              <span className="text-sm text-[#6b6b6b]">Custom:</span>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="text-sm text-[#3d3d3d] outline-none flex-1"
              />
            </div>
          </div>

          {/* Reminder Settings */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="send-reminder"
                checked={sendReminder}
                onChange={(e) => setSendReminder(e.target.checked)}
                className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
              />
              <label htmlFor="send-reminder" className="text-sm text-[#2d2d2d] font-medium">
                Send reminder notification
              </label>
            </div>

            {sendReminder && (
              <div className="pl-7">
                <div className="flex flex-wrap gap-2">
                  {reminderOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setReminderTime(option.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        reminderTime === option.value
                          ? "bg-[#704aaf] text-white"
                          : "bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] hover:bg-[#f0ebf5]"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info Note */}
          <div className="p-3 bg-[#e6f7ed] border border-[#b8e6cd] rounded-lg">
            <div className="flex items-start gap-2">
              <Bell className="w-4 h-4 text-[#0d7a3e] mt-0.5" />
              <p className="text-sm text-[#0d7a3e]">
                The treatment status will change to "In Progress" at the scheduled time.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
            >
              <Play className="w-4 h-4" />
              Schedule Start
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


